import React, { useState } from 'react';
import { X, Camera, AlertCircle, CheckCircle, Loader } from 'lucide-react';
import evidenceAPI from '../../services/evidenceAPI';

interface CaptureScreenshotModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  projectId?: number;
}

interface ControlTemplate {
  id: string;
  name: string;
  icon: string;
  description: string;
  defaultUrl: string;
  complianceTags: string[];
}

const CaptureScreenshotModal: React.FC<CaptureScreenshotModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  projectId
}) => {
  const [captureMode, setCaptureMode] = useState<'control' | 'custom'>('control');
  const [selectedControl, setSelectedControl] = useState<string>('');
  const [url, setUrl] = useState('');
  const [evidenceName, setEvidenceName] = useState('');
  const [description, setDescription] = useState('');
  const [purpose, setPurpose] = useState('');
  const [complianceTags, setComplianceTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [fullPage, setFullPage] = useState(true);
  const [addWatermark, setAddWatermark] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const baseUrl = window.location.origin;

  const controlTemplates: ControlTemplate[] = [
    {
      id: 'mfa',
      name: 'MFA Enforcement Screen',
      icon: '🔐',
      description: 'Admin panel showing MFA requirement settings',
      defaultUrl: `${baseUrl}/app/settings/security/mfa`,
      complianceTags: ['OWASP_A07', 'ISO27001_A9', 'PCI_DSS_8', 'SOC2_CC6']
    },
    {
      id: 'firewall',
      name: 'Firewall Configuration',
      icon: '🔥',
      description: 'Network security and firewall rules dashboard',
      defaultUrl: `${baseUrl}/app/settings/network/firewall`,
      complianceTags: ['PCI_DSS_1', 'ISO27001_A13', 'NIST_PR_AC', 'SOC2_CC6']
    },
    {
      id: 'access_control',
      name: 'Access Control Settings',
      icon: '👥',
      description: 'User permissions and role-based access control',
      defaultUrl: `${baseUrl}/app/settings/users/access-control`,
      complianceTags: ['SOC2_CC6', 'ISO27001_A9', 'PCI_DSS_7', 'GDPR_SECURITY']
    },
    {
      id: 'encryption',
      name: 'Encryption Configuration',
      icon: '🔒',
      description: 'Data encryption and TLS/SSL settings',
      defaultUrl: `${baseUrl}/app/settings/security/encryption`,
      complianceTags: ['OWASP_A04', 'PCI_DSS_4', 'ISO27001_A14', 'SOC2_CC6']
    },
    {
      id: 'backup',
      name: 'Backup Status Dashboard',
      icon: '💾',
      description: 'Backup schedules and recovery testing status',
      defaultUrl: `${baseUrl}/app/operations/backup`,
      complianceTags: ['SOC2_CC7', 'ISO27001_A12', 'PCI_DSS_9']
    },
    {
      id: 'audit_logs',
      name: 'Audit Logs Dashboard',
      icon: '📊',
      description: 'System audit trail and logging configuration',
      defaultUrl: `${baseUrl}/app/audit/logs`,
      complianceTags: ['SOC2_CC7', 'ISO27001_A12', 'PCI_DSS_10', 'GDPR_SECURITY']
    }
  ];

  const commonTags = [
    'SOC2_CC1', 'SOC2_CC6', 'SOC2_CC7', 'SOC2_CC8',
    'ISO27001_A9', 'ISO27001_A13', 'ISO27001_A14',
    'PCI_DSS_4', 'PCI_DSS_8', 'PCI_DSS_11',
    'OWASP_A07', 'OWASP_A08', 'OWASP_A04',
    'NIST_PR_AC', 'NIST_PR_DS', 'GDPR_SECURITY'
  ];

  const handleControlSelect = (controlId: string) => {
    const control = controlTemplates.find(c => c.id === controlId);
    if (control) {
      setSelectedControl(controlId);
      setUrl(control.defaultUrl);
      setEvidenceName(control.name);
      setDescription(control.description);
      setComplianceTags(control.complianceTags);
    }
  };

  const handleAddTag = (tag: string) => {
    if (tag && !complianceTags.includes(tag)) {
      setComplianceTags([...complianceTags, tag]);
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setComplianceTags(complianceTags.filter(tag => tag !== tagToRemove));
  };

  const validateUrl = (urlString: string): boolean => {
    try {
      const parsedUrl = new URL(urlString);
      return parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:';
    } catch {
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (!url.trim()) {
      setError('URL is required');
      return;
    }

    if (!validateUrl(url)) {
      setError('Please enter a valid HTTP or HTTPS URL');
      return;
    }

    if (!evidenceName.trim()) {
      setError('Evidence name is required');
      return;
    }

    try {
      setLoading(true);

      await evidenceAPI.captureScreenshot({
        url: url.trim(),
        evidence_name: evidenceName.trim(),
        description: description.trim(),
        purpose: purpose.trim(),
        project_id: projectId,
        compliance_tags: complianceTags,
        capture_options: {
          full_page: fullPage,
          viewport: { width: 1920, height: 1080 },
          wait_until: 'networkidle2',
          delay_ms: 2000
        },
        add_watermark: addWatermark
      });

      setSuccess(true);
      setTimeout(() => {
        onSuccess();
        handleClose();
      }, 1500);

    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to capture screenshot');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setUrl('');
      setEvidenceName('');
      setDescription('');
      setPurpose('');
      setComplianceTags([]);
      setTagInput('');
      setFullPage(true);
      setAddWatermark(true);
      setError(null);
      setSuccess(false);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={handleClose} />

      <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
              <Camera className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Capture Screenshot Evidence</h2>
              <p className="text-sm text-gray-500">Automatically capture and watermark web screenshots</p>
            </div>
          </div>
          <button onClick={handleClose} disabled={loading} className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {success && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-green-800">Screenshot captured successfully!</p>
                <p className="text-xs text-green-600 mt-1">Evidence saved with timestamp watermark</p>
              </div>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-red-800">Error</p>
                <p className="text-xs text-red-600 mt-1">{error}</p>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">Capture Method</label>
            <div className="flex gap-3">
              <button type="button" onClick={() => setCaptureMode('control')} disabled={loading}
                className={`flex-1 px-4 py-2 rounded-lg border-2 font-medium ${captureMode === 'control' ? 'border-indigo-600 bg-indigo-50 text-indigo-700' : 'border-gray-300 text-gray-700 hover:border-gray-400'}`}>
                📋 Select Control
              </button>
              <button type="button" onClick={() => {setCaptureMode('custom'); setSelectedControl('');}} disabled={loading}
                className={`flex-1 px-4 py-2 rounded-lg border-2 font-medium ${captureMode === 'custom' ? 'border-indigo-600 bg-indigo-50 text-indigo-700' : 'border-gray-300 text-gray-700 hover:border-gray-400'}`}>
                🔗 Custom URL
              </button>
            </div>
          </div>

          {captureMode === 'control' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Which control do you want to screenshot?</label>
              <div className="grid grid-cols-2 gap-3">
                {controlTemplates.map((control) => (
                  <button key={control.id} type="button" onClick={() => handleControlSelect(control.id)} disabled={loading}
                    className={`p-3 rounded-lg border-2 text-left transition-all ${selectedControl === control.id ? 'border-indigo-600 bg-indigo-50' : 'border-gray-200 hover:border-indigo-300'}`}>
                    <div className="flex items-start gap-2">
                      <span className="text-2xl">{control.icon}</span>
                      <div className="flex-1">
                        <div className="font-medium text-sm text-gray-900">{control.name}</div>
                        <div className="text-xs text-gray-500 mt-0.5">{control.description}</div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              URL to Capture <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder={captureMode === 'control' ? 'Auto-filled from control (editable)' : `${window.location.origin}/app/dashboard`}
              disabled={loading}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:bg-gray-100"
            />
            <p className="text-xs text-gray-500 mt-1">{captureMode === 'control' ? 'Edit URL if needed' : 'Enter the full URL (HTTPS recommended)'}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Evidence Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={evidenceName}
              onChange={(e) => setEvidenceName(e.target.value)}
              placeholder="e.g., MFA Enforcement Dashboard Screenshot"
              disabled={loading}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:bg-gray-100"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe what this screenshot demonstrates..."
              rows={3}
              disabled={loading}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:bg-gray-100 resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Purpose</label>
            <input
              type="text"
              value={purpose}
              onChange={(e) => setPurpose(e.target.value)}
              placeholder="e.g., Q1 2026 SOC 2 Audit"
              disabled={loading}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:bg-gray-100"
            />
            <p className="text-xs text-gray-500 mt-1">Appears in watermark with timestamp</p>
          </div>

          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700">Capture Settings</label>
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="fullPage"
                checked={fullPage}
                onChange={(e) => setFullPage(e.target.checked)}
                disabled={loading}
                className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
              />
              <label htmlFor="fullPage" className="text-sm text-gray-700 cursor-pointer">
                Full page screenshot
              </label>
            </div>
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="addWatermark"
                checked={addWatermark}
                onChange={(e) => setAddWatermark(e.target.checked)}
                disabled={loading}
                className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
              />
              <label htmlFor="addWatermark" className="text-sm text-gray-700 cursor-pointer">
                Add timestamp watermark (recommended)
              </label>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Compliance Tags</label>
            {complianceTags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {complianceTags.map((tag) => (
                  <span key={tag} className="inline-flex items-center gap-1 px-3 py-1 bg-indigo-100 text-indigo-700 text-xs font-medium rounded-full">
                    {tag}
                    <button type="button" onClick={() => handleRemoveTag(tag)} disabled={loading} className="hover:text-indigo-900">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value.toUpperCase())}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag(tagInput))}
                placeholder="Type tag or select below"
                disabled={loading}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-sm disabled:bg-gray-100"
              />
              <button
                type="button"
                onClick={() => handleAddTag(tagInput)}
                disabled={loading || !tagInput}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 text-sm font-medium"
              >
                Add
              </button>
            </div>
            <div className="mt-3">
              <p className="text-xs text-gray-500 mb-2">Common tags:</p>
              <div className="flex flex-wrap gap-2">
                {commonTags.filter(tag => !complianceTags.includes(tag)).slice(0, 8).map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => handleAddTag(tag)}
                    disabled={loading}
                    className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded hover:bg-gray-200 disabled:opacity-50"
                  >
                    + {tag}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex gap-3">
              <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">Screenshot Details:</p>
                <ul className="list-disc list-inside space-y-1 text-xs text-blue-700">
                  <li>Watermark: Timestamp, user, purpose, evidence ID</li>
                  <li>PNG with SHA-256 hash for tamper detection</li>
                  <li>90-day staleness review tracking</li>
                  <li>Capture takes 10-30 seconds</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !url || !evidenceName}
              className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 font-medium flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader className="w-4 h-4 animate-spin" />
                  Capturing...
                </>
              ) : (
                <>
                  <Camera className="w-4 h-4" />
                  Capture & Save Evidence
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CaptureScreenshotModal;
